import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import FundraiserDashboard from "./pages/FundraiserDashboard";
import FundraiserRecords from "./pages/FundraiserRecords";
import OwnerDashboard from "./pages/OwnerDashboard";
import PlatformDashboard from "./pages/PlatformDashboard";
import ConversionForm from "./pages/ConversionForm";
import TrainingPage from "./pages/TrainingPage";
import FundraiserDetail from "./pages/FundraiserDetail";
import ReferralLanding from "./pages/ReferralLanding";
import OneOffSuccess from "./pages/OneOffSuccess";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import LandingPage from "./pages/LandingPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import CommercialManagementConsole from "./pages/CommercialManagementConsole";
import ValidationManagementConsole from "./pages/ValidationManagementConsole";
import StatementManagementConsole from "./pages/StatementManagementConsole";
import FundraiserPassportCentre from "./pages/FundraiserPassportCentre";
import ClientPortal from "./pages/ClientPortal";
import OperationalRecord from "./pages/OperationalRecord";
import MyPerformanceRecord from "./pages/MyPerformanceRecord";
import MySchedule from "./pages/MySchedule";
import OnboardingTour from "./components/OnboardingTour";
import TeamLeaderDashboard from "./components/TeamLeaderDashboard";
import { landingPathFor, isLeadership, useTeamLeaderDashboard, canFundraiserSelfService } from "./lib/roleAccess";

const BACKEND_URL = window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// API instance with auth
export const apiClient = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" }
});

apiClient.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("taptrack_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem("taptrack_token");
      sessionStorage.removeItem("taptrack_user");
      window.location.href = "/login";
    }
    // Log all API errors for debugging (non-401, non-production)
    if (error.response?.status !== 401 && process.env.NODE_ENV !== "production") {
      console.error(`API Error [${error.response?.status}]: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, error.response?.data?.detail || error.message);
    }
    return Promise.reject(error);
  }
);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("taptrack_user");
      const token = sessionStorage.getItem("taptrack_token");
      
      if (storedUser && token) {
        setUser(JSON.parse(storedUser));
      }
    } catch (e) {
      sessionStorage.removeItem("taptrack_user");
      sessionStorage.removeItem("taptrack_token");
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      if (process.env.NODE_ENV !== "production") {
        console.log("LOGIN REQUEST:", { url: `${API}/auth/login`, email, password: "***" });
      }
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (process.env.NODE_ENV !== "production") {
        console.log("LOGIN RESPONSE:", { status: response.status, instance_id: response.data?.instance_id, hasToken: !!response.data?.token, role: response.data?.user?.role });
      }
      const { token, user: userData } = response.data;
      sessionStorage.setItem("taptrack_token", token);
      sessionStorage.setItem("taptrack_user", JSON.stringify(userData));
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.error("LOGIN ERROR:", { status: error.response?.status, detail: error.response?.data?.detail, url: `${API}/auth/login` });
      }
      const raw = error.response?.data?.detail;
      const status = error.response?.status;
      let friendly;
      if (status === 401 || status === 403) {
        friendly = "The email or password you entered didn't match our records. Please try again or use \"Forgot your password?\" to reset it. If the problem continues, your account may be inactive — contact your organisation administrator.";
      } else if (status === 429) {
        friendly = "Too many sign-in attempts. Please wait a few minutes and try again.";
      } else if (typeof raw === "string" && raw) {
        friendly = raw;
      } else if (raw && typeof raw === "object" && raw.message) {
        friendly = raw.message;
      } else {
        friendly = "We couldn't sign you in just now. Please try again shortly. If the problem continues, contact your organisation administrator.";
      }
      return { success: false, error: friendly };
    }
  };

  const register = async (data) => {
    try {
      const response = await axios.post(`${API}/auth/register`, data);
      const { token, user: userData } = response.data;
      sessionStorage.setItem("taptrack_token", token);
      sessionStorage.setItem("taptrack_user", JSON.stringify(userData));
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || "Registration failed" };
    }
  };

  const logout = () => {
    sessionStorage.removeItem("taptrack_token");
    sessionStorage.removeItem("taptrack_user");
    setUser(null);
    // Hard navigate to clear all in-memory dashboard state
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles, allow }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // OCI V2.19 — tier-aware guard (honours elevated-fundraiser leadership).
  const permitted = allow ? allow(user) : (!allowedRoles || allowedRoles.includes(user.role));
  if (!permitted) {
    return <Navigate to={landingPathFor(user)} replace />;
  }

  return children;
};

// Home redirect based on canonical access tier
const HomeRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={landingPathFor(user)} replace />;
};

// Redirect already-authenticated users away from login/register
const AuthRedirect = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    return <Navigate to={landingPathFor(user)} replace />;
  }
  return children;
};

// OCI V2.19 — /owner area picks the right management surface by access tier.
const OwnerArea = () => {
  const { user } = useAuth();
  return useTeamLeaderDashboard(user) ? <TeamLeaderDashboard /> : <OwnerDashboard />;
};

// Fundraisers get the self-scoped My Performance Record; management roles get the
// operational/board record. Page selection by role (no management UI for fundraisers).
const OperationalRecordRoute = () => {
  const { user } = useAuth();
  return user?.role === "fundraiser" ? <MyPerformanceRecord /> : <OperationalRecord />;
};

// Statement-Only Mode Consistency Pass: the previous internal-status banner
// has been removed. Internal implementation status is no longer surfaced to
// end users. Backend guards (Package 0 Safety Freeze feature-mode flags)
// remain authoritative regardless of banner presence.

function App() {
  return (
    <AuthProvider>
      <div className="App font-body">
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            style: { fontFamily: "'IBM Plex Sans', sans-serif" }
          }}
        />
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<AuthRedirect><LoginPage /></AuthRedirect>} />
            <Route path="/register" element={<AuthRedirect><RegisterPage /></AuthRedirect>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/r/:code" element={<ReferralLanding />} />
            <Route path="/one-off-success" element={<OneOffSuccess />} />
            
            {/* Protected Routes - HomeRedirect for logged in users */}
            <Route path="/home" element={<HomeRedirect />} />

            <Route
              path="/fundraiser/passport"
              element={
                <ProtectedRoute allow={canFundraiserSelfService}>
                  <FundraiserPassportCentre />
                </ProtectedRoute>
              }
            />
            
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute allowedRoles={["fundraiser", "owner", "manager"]}>
                  <FundraiserDashboard />
                </ProtectedRoute>
              } 
            />

            <Route
              path="/records"
              element={
                <ProtectedRoute allow={canFundraiserSelfService}>
                  <FundraiserRecords />
                </ProtectedRoute>
              }
            />

            <Route
              path="/schedule"
              element={
                <ProtectedRoute allow={canFundraiserSelfService}>
                  <MySchedule />
                </ProtectedRoute>
              }
            />
            
            <Route 
              path="/conversion/:interactionId" 
              element={
                <ProtectedRoute allow={canFundraiserSelfService}>
                  <ConversionForm />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/training" 
              element={
                <ProtectedRoute allow={canFundraiserSelfService}>
                  <TrainingPage />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/owner" 
              element={
                <ProtectedRoute allow={isLeadership}>
                  <OwnerArea />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/owner/fundraiser/:fundraiserId" 
              element={
                <ProtectedRoute allow={isLeadership}>
                  <FundraiserDetail />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/platform" 
              element={
                <ProtectedRoute allowedRoles={["platform_owner"]}>
                  <PlatformDashboard />
                </ProtectedRoute>
              } 
            />

            <Route
              path="/client"
              element={
                <ProtectedRoute allowedRoles={["client"]}>
                  <ClientPortal />
                </ProtectedRoute>
              }
            />

            {/* OCI V2.7B — Charity Portal (reuses ClientPortal, charity-scoped) */}
            <Route
              path="/charity"
              element={
                <ProtectedRoute allowedRoles={["charity_user"]}>
                  <ClientPortal />
                </ProtectedRoute>
              }
            />

            {/* Operational Record — fundraisers get a self-scoped performance record;
                management roles get the operational/board record. */}
            <Route
              path="/operational-record"
              element={
                <ProtectedRoute allowedRoles={["owner", "manager", "platform_owner", "client", "fundraiser"]}>
                  <OperationalRecordRoute />
                </ProtectedRoute>
              }
            />

            {/* Package 2 — Multi-Organisation Contracts & Campaign Engine (Engage v2) */}
            <Route
              path="/engage/commercial-management"
              element={
                <ProtectedRoute allowedRoles={["platform_owner", "platform_administrator",
                                                "owner", "director", "admin",
                                                "manager", "regional_manager"]}>
                  <CommercialManagementConsole />
                </ProtectedRoute>
              }
            />

            {/* Package 3 — Provider-Agnostic Operational Validation Services */}
            <Route
              path="/engage/validation-management"
              element={
                <ProtectedRoute allowedRoles={["platform_owner", "platform_administrator",
                                                "platform_auditor", "platform_analyst",
                                                "platform_support", "owner", "director",
                                                "admin", "manager", "regional_manager"]}>
                  <ValidationManagementConsole />
                </ProtectedRoute>
              }
            />

            {/* Package 5 — Operational Statement & Settlement Engine */}
            <Route
              path="/engage/statements"
              element={
                <ProtectedRoute allowedRoles={["platform_owner", "platform_administrator",
                                                "platform_auditor", "platform_analyst",
                                                "platform_support", "owner", "director",
                                                "admin", "manager", "regional_manager",
                                                "commercial_manager"]}>
                  <StatementManagementConsole />
                </ProtectedRoute>
              }
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          {/* Global guided walkthrough — role-aware, first-login only, replayable */}
          <OnboardingTour />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
